import { getShopperId } from '../util/auth';
import {
  trackLegacyDocumentConversionAttempted,
  trackLegacyDocumentConversionAttemptFailed,
  trackLegacyDocumentConversionSuccess,
  trackReorderAttempted,
  trackReorderAttemptFailed,
  trackReorderSuccess,
  trackBuyNowAttempted,
  trackBuyNowSuccess,
  trackBuyNowAttemptFailed,
} from './newRelicTrackingApi';
import {
  convertLegacyDocumentAttempted,
  orderReorderV2Attempted,
  expressCheckoutBuyNowAttempted,
} from './trackingApi';
import { dispatchPostRequest } from '../util/apiUtils.ts';

const prodReorderBaseUrl = process.env.PRODUCTION_REORDER_BASE_URL;
const stagingReorderBaseUrl = process.env.STAGING_REORDER_BASE_URL;

/**
 * Returns an object containing parameters to use for calling the reorder service.
 * In the future, consider adding timeouts as part of the configuration.
 * @param {*} environment The environment to send reorder service requests too.
 */
function getConfigForEnvironment(environment) {
  if (!environment) {
    console.error(
      `Unable to derive service config for a null/empty environment, using STAGING environment as sensible fallback.`
    );
    return { baseUrl: stagingReorderBaseUrl };
  }
  switch (environment) {
    case 'PRODUCTION':
      return { baseUrl: prodReorderBaseUrl };
    case 'STAGING':
      return { baseUrl: stagingReorderBaseUrl };
    default:
      console.warn(
        `Unsupported environment type of ${environment}, using STAGING environment as sensible fallback.`
      );
      return { baseUrl: stagingReorderBaseUrl };
  }
}

/**
 * Checks to see if at least one result in the provided results array, was successful
 * @param {{successful: boolean}[]} results results from request
 * @returns {boolean} whether or not at least one of the results was successful
 */
const hasAtLeastOneSuccessfulResult = results =>
  results && results.some(result => result.successful === true);

/**
 * When requesting to reorder multiple items, there can sometimes be partial failure/success.
 * This method reads the response and determines if the entire request failed or not.
 * If it was not a success, it will throw an exception.
 * @param {Response} response
 */
const ensureReorderRequestWasSuccessfulOrThrow = async response => {
  if (response && response.status === 200) {
    const data = await response.json();
    const { results } = data;
    if (hasAtLeastOneSuccessfulResult(results)) {
      return data;
    }
    // throw exception with the failed results
    const errorObj = {
      results,
    };
    throw errorObj;
  }
  throw new Error('request to reorder failed');
};

/**
 * Invoke the reorderService to reorder this set of lineItem(s)
 * @param {*} environment - production or staging?
 * @param {*} orderNumber - order that is being reordered
 * @param {*} reorderItems - list of lineItem IDs that are part of the reorder
 * @param {*} tenantId - which tenant cart the items should be going into
 * @param {*} reorderSourcePage - which page did the reorder request come from (OD vs OH)
 */
export const reorderItemsWithSpecifiedQuantities = async ({
  tenantEnvironment,
  orderNumber,
  reorderItems,
  vistacartTenantName,
  reorderSourcePage,
}) => {
  const { baseUrl } = getConfigForEnvironment(tenantEnvironment);
  const shopperId = getShopperId();
  const metricsAdditionalData = {
    orderId: orderNumber,
    cartTenant: vistacartTenantName,
    page: reorderSourcePage,
    lineItemIds: reorderItems.map(item => item.lineItemId),
  };
  const url = `${baseUrl}/v2/reorder/shoppers/${shopperId}/orders/${orderNumber}/cartTenant/${vistacartTenantName}`;

  // notify segment for analytics
  orderReorderV2Attempted(orderNumber, reorderItems);
  // notify NR for tracking/monitoring
  trackReorderAttempted(shopperId, metricsAdditionalData);

  try {
    const response = await dispatchPostRequest({ url, body: reorderItems });
    await ensureReorderRequestWasSuccessfulOrThrow(response);
    trackReorderSuccess(shopperId, metricsAdditionalData);
  } catch (err) {
    trackReorderAttemptFailed(err, shopperId, metricsAdditionalData);
    throw err;
  }
};

/**
 * Invoke the reorderService to buy this item now
 * @param {*} environment - production or staging?
 * @param {*} orderNumber - order that is being reordered
 * @param {*} reorderItems - list of lineItem IDs that are part of the reorder
 * @param {*} tenantId - which tenant cart the items should be going into
 * @param {*} reorderSourcePage - which page did the reorder request come from
 * @param {*} reorderSourcePageName - the name of the page the reorder request come from
 */
export const reorderForAltCheckout = async ({
  tenantEnvironment,
  orderNumber,
  reorderItems,
  vistacartTenantName,
  reorderSourcePage,
  reorderSourcePageName,
}) => {
  const { baseUrl } = getConfigForEnvironment(tenantEnvironment);
  const shopperId = getShopperId();
  const metricsAdditionalData = {
    orderNumber,
    cartTenant: vistacartTenantName,
    page: reorderSourcePage,
    lineItemIds: reorderItems.map(item => item.lineItemId),
  };
  // construct the request body from "reorderItems"
  const body = reorderItems.map(({ lineItemId, quantity, analytics }) => ({
    lineItemId,
    quantity,
    analytics,
  }));
  const url = `${baseUrl}/reorderForAltCheckout/shoppers/${shopperId}/orders/${orderNumber}/cartTenant/${vistacartTenantName}`;

  // notify segment for analytics
  expressCheckoutBuyNowAttempted(
    orderNumber,
    reorderItems,
    reorderSourcePageName
  );

  // notify NR for tracking/monitoring
  trackBuyNowAttempted(shopperId, metricsAdditionalData);

  try {
    const response = await dispatchPostRequest({ url, body });
    const data = await ensureReorderRequestWasSuccessfulOrThrow(response);
    trackBuyNowSuccess(shopperId, metricsAdditionalData);
    return data;
  } catch (err) {
    trackBuyNowAttemptFailed(err, shopperId, metricsAdditionalData);
    throw err;
  }
};

/**
 * Invoke OREOs to convert the legacy document for a line item
 * @param {string} environment - production or staging?
 * @param {string} orderNumber - order that is being reordered
 * @param {string} lineItemId - lineItem ID that are part of the reorder
 * @param {string} tenant - which tenant cart the items should be going into
 * @param {string} sourcePage - which page did the reorder request come from (OD vs OH)
 */
export const convertLegacyDocument = async ({
  tenantEnvironment,
  orderNumber,
  lineItemId,
  tenant,
  sourcePage,
}) => {
  const { baseUrl } = getConfigForEnvironment(tenantEnvironment);
  const shopperId = getShopperId();
  const metricsAdditionalData = {
    orderId: orderNumber,
    itemId: lineItemId,
    cartTenant: tenant,
    page: sourcePage,
  };
  const url = `${baseUrl}/v2/shoppers/${shopperId}/orders/${orderNumber}/items/${lineItemId}/convertLegacyDocument`;
  const conversionError = new Error(
    'request to convert a legacy document failed'
  );
  // notify segment for analytics
  convertLegacyDocumentAttempted({ orderNumber, lineItemId });

  // notify NR for tracking/monitoring
  trackLegacyDocumentConversionAttempted(shopperId, metricsAdditionalData);

  try {
    const response = await dispatchPostRequest({ url });
    const data = await response.json();
    if (data.successful) {
      trackLegacyDocumentConversionSuccess(shopperId, metricsAdditionalData);
    } else {
      trackLegacyDocumentConversionAttemptFailed(
        conversionError,
        shopperId,
        metricsAdditionalData
      );
    }
    return data;
  } catch (err) {
    trackLegacyDocumentConversionAttemptFailed(
      conversionError,
      shopperId,
      metricsAdditionalData
    );
    throw conversionError;
  }
};
